import { addToast } from "components/ToastContainer/ToastStore";
import { routeConstants } from "constants/routeConstants";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "slice";
import {
    useGetProfileStrengthQuery,
    useGetUserPhotosQuery,
    usePutOrDeleteUserPhotoMutation,
    useSetUserPhotosMutation,
} from "slice/user";
import { Image } from "./types";

export const usePhotosAndVideos = () => {
    const navigateTo = useNavigate();
    const { id: user_id } = useAppSelector((state) => state.user);
    const { data, isFetching, refetch } = useGetUserPhotosQuery(user_id);
    const [userImages, setUserImages] = useState<Image[]>([]);
    const [imageBeingDeleted, setImageBeingDeleted] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { refetch: refetchProfileStrength } = useGetProfileStrengthQuery(
        user_id ?? "",
        {
            skip: !user_id,
        }
    );
    const [uploadFile, { isSuccess, isLoading, data: newImageData }] =
        useSetUserPhotosMutation();
    const [
        deleteFile,
        { isSuccess: isDeleted, isLoading: isDeleting, isError: didNotDelete },
    ] = usePutOrDeleteUserPhotoMutation();

    const toggleModal = () => setIsModalOpen((prev) => !prev);
    const handleBack = () => {
        navigateTo(
            routeConstants.profile.editProfile.root +
                routeConstants.profile.editProfile.personalDetails
        );
    };

    const handleSaveAndNext = () => {
        navigateTo(
            routeConstants.profile.editProfile.root +
                routeConstants.profile.editProfile.physicalDetails
        );
    };

    const handleDeleteImage = (id: string) => {
        setImageBeingDeleted(id);
        deleteFile({
            user_id,
            image_id: id,
        });
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.length) {
            const file = e.target.files[0];
            const acceptedFileTypes = ["image/png", "image/jpg", "image/jpeg"];
            if (!acceptedFileTypes.includes(file.type)) {
                addToast({
                    type: "error",
                    text: "Invalid files selected. Only jpg, png or jpeg files are accepted.",
                });

                return;
            }

            const formData = new FormData();
            formData.append("image", e.target.files[0]);
            formData.append("image_type", "portfolio");
            formData.append("user_id", `${user_id}`);

            try {
                const response : any = await uploadFile({
                    method: "POST",
                    body: formData,
                    user_id: user_id,
                });

                if (response.error) {
                    if (response.error.status === 422) {
                        addToast({
                            type: "error",
                            text: response.error.message || "You've reached the image upload limit for your subscription.",
                        });
                    } else {
                        addToast({
                            type: "error",
                            text: "An unexpected error occurred. Please try again.",
                        });
                    }
                } else {
                    refetchProfileStrength();
                }
            } catch (error) {
                addToast({
                    type: "error",
                    text: "An unexpected error occurred. Please try again.",
                });
            }

            // setUserImages(prev => ([
            //     ...prev,
            //     {
            //         "id": "a",
            //         "image_type": "a",
            //         "user_id": user_id,
            //         "created_at": "",
            //         "updated_at": "",
            //         "url": ""
            //     }
            // ]))
        }
    };
    useEffect(() => {
        refetch();
    }, [refetch]);
    useEffect(() => {
        if (isSuccess) {
            setUserImages((prev) => [
                ...prev,
                {
                    id: newImageData.data.id,
                    image_type: newImageData.data.attributes.id,
                    user_id: user_id,
                    created_at: "",
                    updated_at: "",
                    url: newImageData.data.attributes.url,
                },
            ]);
        }
    }, [isSuccess, newImageData, user_id]);

    useEffect(() => {
        if (data && data.length) {
            setUserImages(
                data
                    .filter((img: any) => img.image_type === "portfolio")
                    .map((img: any) => ({
                        id: img.id,
                        url: img.url,
                    }))
            );
        }
    }, [data]);

    useEffect(() => {
        if (didNotDelete) {
            addToast({
                type: "error",
                text: "Something went wrong. Please try again.",
            });
        }
    }, [didNotDelete]);

    useEffect(() => {
        if (isDeleted && imageBeingDeleted !== "") {
            setUserImages((prev) =>
                prev.filter((img) => img.id !== imageBeingDeleted)
            );
            setImageBeingDeleted("");
        }
    }, [imageBeingDeleted, isDeleted]);

    return {
        imageBeingDeleted,
        isDeleting,
        isFetching,
        isLoading,
        userImages,
        isModalOpen,
        toggleModal,
        handleBack,
        handleSaveAndNext,
        handleFileUpload,
        handleDeleteImage,
    };
};
